import React from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PartyPopper, CheckCircle, ArrowRight } from 'lucide-react';
import { Playbook } from '../types';

interface PlaybookCompletionDialogProps {
  playbook: Playbook;
  isOpen: boolean;
  onClose: () => void;
  onViewPlaybook: () => void;
}

const PlaybookCompletionDialog: React.FC<PlaybookCompletionDialogProps> = ({
  playbook,
  isOpen,
  onClose,
  onViewPlaybook
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md rounded-2xl border border-zinc-200 shadow-xl p-0 overflow-hidden">
        <div className="bg-primary p-8 text-white text-center">
          <div className="flex justify-center mb-6">
            <div className="h-20 w-20 bg-white/20 rounded-full flex items-center justify-center shadow-lg animate-[pulse_3s_ease-in-out_infinite]">
              <PartyPopper className="h-10 w-10" />
            </div>
          </div>
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-white mb-3">
              Congratulations!
            </DialogTitle>
          </DialogHeader>
          <p className="text-lg text-white mb-2">
            You've completed the entire <span className="font-bold">{playbook.title}</span> playbook!
          </p>
          <p className="text-base text-blue-100">
            This will help you {playbook.outcomes[0].toLowerCase()}
          </p>
        </div>
        
        <div className="p-8">
          <h3 className="text-xl font-bold text-zinc-900 mb-4 flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-500" />
            What you've achieved
          </h3>
          
          <ul className="space-y-3 mb-6">
            {playbook.outcomes.slice(0, 3).map((outcome, index) => (
              <li key={index} className="flex items-start gap-3">
                <div className="bg-primary/10 rounded-full p-1 mt-0.5">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-primary">
                    <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-zinc-700">{outcome}</span>
              </li>
            ))}
          </ul>
          
          <h3 className="text-xl font-bold text-zinc-900 mb-4 flex items-center gap-2">
            <ArrowRight className="h-5 w-5 text-primary" />
            What's next?
          </h3>
          <p className="text-zinc-700 mb-6">
            You can return to review this playbook anytime, or explore other playbooks to further enhance your skills.
          </p>
        </div>
        
        <DialogFooter className="px-8 pb-8 pt-0">
          <div className="flex flex-col sm:flex-row gap-4 w-full">
            <Button 
              className="flex-1 bg-primary hover:bg-primary/90 transition-all duration-200 rounded-lg text-white font-medium py-6 h-12 shadow-sm"
              onClick={onViewPlaybook}
            >
              View Playbook Summary
            </Button>
            <Button 
              variant="outline" 
              className="flex-1 border-zinc-300 hover:bg-zinc-50 transition-all duration-200 rounded-lg text-zinc-700 font-medium py-6 h-12"
              onClick={onClose}
            >
              Close
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PlaybookCompletionDialog; 